<script lang="ts">
    import { navigate, useLocation } from "svelte-navigator";
    import { afterUpdate, onMount, onDestroy } from "svelte";
    import { api, cart, loader, toast, config } from "../stores";
    import type { CartProduct, CartType, Operation, Product } from "../model";
    import moment from "moment";
    import CatalogNotFound from "../CatalogNotFound.svelte";
    import ProductPrice from "./ProductPrice.svelte";
    import ProductCase from "./ProductCase.svelte";
    import PastillePromotion from "./PastillePromotion.svelte";
    import { YoutubeService } from "../services/YoutubeService";
    import Carousel from "../Carousel.svelte";
    import ProductCarousel from "./ProductCarousel.svelte";
    import InputQuantity from "../components/InputQuantity.svelte";

    export let product: Product = null;
    export let productId = null;

    let light = false;
    export let sendEvents = true;
    export let showCartButtons = true;
    export let showBackButton = true;
    export let crossSell = true;

    let operation: Operation = null;
    let otherProducts: Product[] = null;
    let cartProduct: CartProduct;
    let mouseInBottomBar = false;
    let imgViewerUrl = "";
    let imgViewerActive = 0;
    let videoViewerUrl = "";
    let videoViewerActive = -1;
    let added = false;
    let crossSellLoader = false;

    let medias: { type: "image" | "video"; url: string }[] = [];
    let carousel;
    let loading;
    $: loading = $loader.loading; // the loading status in live
    let nbcol = 5;
    let showCarousel = false;
    let innerWidth = 1107;

    const mainContent = document.getElementById("main-content");

    const handleImgError = (ev) => (ev.target.src = "/no_visual.jpg");
    const location = useLocation();

    let opensPart = {
        description: true,
        docs: true,
    };

    $: () => {
        console.log($location);
    };

    $: imgViewerUrl =
        product && product.images && product.images.length > 0
            ? product.images[0]
            : "";

    $: currentMedia = medias[imgViewerActive];

    /**
     * define number of column to otherProduct carousel
     */
    $: if (innerWidth) {
        if (innerWidth < 650) {
            nbcol = 1;
        } else if (innerWidth < 1107) {
            nbcol = 2;
        } else if (innerWidth < 1461) {
            nbcol = 3;
        } else if (innerWidth < 1815) {
            nbcol = 4;
        } else {
            nbcol = 5;
        }
    }

    // quanity of product
    let quantity = 0;

    onMount(async () => {
        const searchParams = new URLSearchParams(window.location.search);
        for (let p of searchParams) {
            if (p[0] === "light" && p[1] === "true") {
                light = true;
                showBackButton = false;
                showCartButtons = false;
                sendEvents = false;
                crossSell = false;
            }
        }

        // UPDATE cart product when list change
        $cart.list.subscribe(async (list) => {
            if (product) {
                getProductCart();
            }
        });

        if (product === null) {
            init(productId);
        } else {
            initCarousel();
            if (product.images && product.images.length > 0) {
                imgViewerUrl = product.images[0];
            }
        }
    });

    afterUpdate(() => {
        // refresh the component when the product id change
        if (productId && product && productId !== product.uid) {
            init(productId);
        }
    });

    /**
     * Initialization feature
     * @param id
     */
    function init(id: string) {
        const loaderId = $loader.show();
        added = false;
        showCarousel = false;
        $api.getProduct(id).then(
            (result) => {
                product = result;
                if (
                    !operation ||
                    $api.getShopId() === "0000" ||
                    product["france" + operation.shopZone]
                ) {
                    if (showCartButtons) {
                        getProductCart();
                    }

                    if (crossSell) {
                        getOtherProducts();
                    }
                    initCarousel();
                } else {
                    product = null;
                }
                $loader.hide(loaderId);
            },
            (err) => {
                console.error(err);
                $loader.hide(loaderId);
            }
        );
        // scroll to top on init
        mainContent.scrollTop = 0;
    }

    /**
     * get cart product
     */
    function getProductCart(): void {
        let newCart = $cart.getProduct(product.uid);
        // if (!cartProduct && newCart && newCart.quantity != 0) {
        //     $toast.success(
        //         '"' +
        //         product.name.toLowerCase() +
        //         '" à été ajouté dans votre ' +
        //         getTypeMessage(newCart.cartType) +
        //         "."
        //     );
        // }
        cartProduct = newCart;
        quantity = 0;
    }

    function initCarousel() {
        medias = [];
        if (product.images && product.images.length > 0) {
            for (let image of product.images) {
                medias = [...medias, { type: "image", url: image }];
            }
        }
        if (product.videos && product.videos.length > 0) {
            for (let video of product.videos) {
                medias = [...medias, { type: "video", url: video }];
            }
        }
        showCarousel = true;
    }

    /**
     * Feature for add a product in shopping list or in click and collect cart
     * @param cartType
     */
    function onAddProduct(cartType: CartType, qteToAdd: number = 1) {
        $cart.add(product, qteToAdd, cartType);
        getProductCart();
    }

    /**
     * get type message in function of cart type
     * @param cartType
     */
    function getTypeMessage(cartType: string): string {
        let typeMsg = "panier";
        switch (cartType) {
            case "shoppingList":
                typeMsg = "panier";
                break;
            case "clickCollect":
                typeMsg = "panier click and collect";
                break;
        }
        return typeMsg;
    }

    /**
     * Feature for remove à product in cart
     */
    function onRemoveProduct(qteToRemove: number = 1): void {
        $cart.remove(cartProduct, qteToRemove);
        getProductCart();
        if (cartProduct && cartProduct.quantity === 0) {
            cartProduct = null;
            quantity = 0;
        }
    }

    /**
     * Feature for change a product between the shopping list and the click and collect cart
     * @param target
     */
    function changeCartType(target: CartType) {
        $cart.setType(cartProduct, target);
    }

    /**
     * Get the other products list in term of the category child id fo this product
     */
    function getOtherProducts() {
        crossSellLoader = true;
        let body = [
            {
                categoryChildUid: {
                    values: [product.categoryChildUid],
                    matchLevel: "exactly",
                },
            },
        ];
        $api.getProducts(body).then(
            (result) => {
                otherProducts = result;
                crossSellLoader = false;
            },
            (err) => {
                console.error(err);
                crossSellLoader = false;
            }
        );
    }

    /**
     * scroll vertically the thumbnails when mouse is on other products list
     * @param event
     */
    function wheelEvent(event) {
        if (mouseInBottomBar) {
            const scrollContainer = document.querySelector(
                "#list-other-products"
            );
            let eventScroll = event.deltaY;
            if (scrollContainer) {
                scrollContainer.scrollLeft += eventScroll;
            }
        }
    }

    /**
     * Change the picture with the selected
     * @param index
     */
    function onChangeImgViewer(index: number) {
        imgViewerUrl = product.images[index];
        imgViewerActive = index;
        carousel.go(imgViewerActive);
        videoViewerUrl = "";
        videoViewerActive = -1;
    }

    /**
     * select image preview, event from carousel
     */
    function selectIndexImage(event) {
        let index = event.detail.currentSlide;
        onChangeImgViewer(index);
    }

    /**
     * format name document
     * @param name
     */
    function formatName(name: string): string {
        return name.replace(/\.[^/.]+$/, "");
    }

    /**
     * ddl doc file
     * @param filename
     * @param url
     */
    function ddlFile(filename: string, url: string): void {
        window.open(`${url}`, "_blank");
    }

    /**
     * select previous image on thumbnails
     */
    function selectPrevImage() {
        if (imgViewerActive != 0) {
            imgViewerUrl = medias[imgViewerActive - 1].url;
            imgViewerActive = imgViewerActive - 1;
        } else {
            imgViewerUrl = medias[medias.length - 1].url;
            imgViewerActive = medias.length - 1;
        }
        carousel.left();
    }

    /**
     * select next image on thumbnails
     */
    function selectNextImage() {
        if (imgViewerActive != medias.length - 1) {
            imgViewerUrl = medias[imgViewerActive + 1].url;
            imgViewerActive = imgViewerActive + 1;
        } else {
            imgViewerUrl = medias[0].url;
            imgViewerActive = 0;
        }
        carousel.right();
    }

    /**
     * on arrows (left/right) key down, get previous or next image
     * @param event
     */
    function handleKeydown(event) {
        if (!$loading) {
            if (product.images.length > 0) {
                switch (event.keyCode) {
                    case 39: // 39: arrow right
                        if (product.images.length > 1) {
                            selectNextImage();
                        }
                        break;
                    case 37: // 37: arrow left
                        if (product.images.length > 1) {
                            selectPrevImage();
                        }
                        break;
                }
            }
        }
    }

    /**
     * manage qte update
     * @param e : event of input quantity
     */
    function handlerQTE(e): void {
        if (e.detail == null) return;

        quantity = e.detail;
    }

    function qteUpdate() {
        if (quantity == 0) quantity = 1;
        if (!cartProduct) {
            onAddProduct("shoppingList");
        } else {
            updateCartQte();
        }
    }

    /**
     * update cart quanity
     */
    function updateCartQte(): void {
        // Add
        onAddProduct("shoppingList", quantity);
    }

    function openClosePart(key: string) {
        opensPart[key] = !opensPart[key];
    }
</script>

<svelte:window
    bind:innerWidth
    on:wheel={wheelEvent}
    on:keydown={handleKeydown}
/>

{#if product === null}
    <CatalogNotFound title="Ce produit n'est pas disponible pour ce magasin." />
{/if}
{#if product !== null}
    <div id="sheet">
        {#if showBackButton}
            <div class="return mt-1 pl-2">
                <span on:click={() => navigate(-1)}>
                    <i class="fas fa-chevron-left" /> Retour
                </span>
            </div>
        {/if}
        <div class="prod">
            <div class="medias">
                {#if (product.images && product.images.length > 1) || (product.videos && product.videos.length > 0)}
                    <div class="thumbnail-list">
                        {#each medias as media, i}
                            <div
                                class="thumbnail"
                                on:click={() => {
                                    onChangeImgViewer(i);
                                }}
                                class:active={imgViewerActive === i}
                            >
                                {#if media.type == "image"}
                                    <img
                                        on:error={handleImgError}
                                        src={media.url}
                                        alt="Chargement de l'image:{product.name}"
                                    />
                                {:else if media.type == "video"}
                                    <img
                                        on:error={handleImgError}
                                        src={YoutubeService.getThumbailVideoUrl(
                                            media.url
                                        )}
                                        alt="Chargement de la video:{media.url}"
                                    />
                                    <div class="play">
                                        <i class="player-logo fab fa-youtube" />
                                    </div>
                                {/if}
                            </div>
                        {/each}
                    </div>
                {/if}
                <div class="picture">
                    {#if currentMedia}
                        {#if currentMedia.type == "image"}
                            <img
                                class="main-img"
                                alt="Chargement de l'image:{product.name}"
                                class:unavailable={product?.unavailability?.findIndex(
                                    (e) => e === $api.getShopId()
                                ) >= 0}
                                on:error={handleImgError}
                                src={currentMedia.url}
                            />
                        {:else if currentMedia.type == "video"}
                            <iframe
                                allow="encrypted-media"
                                src={YoutubeService.getNoCookieLink(
                                    currentMedia.url
                                )}
                                allowfullscreen
                                frameborder="0"
                                width="100%"
                                height="100%"
                                title="video"
                            />
                        {/if}
                        {#if product?.unavailability?.findIndex((e) => e === $api.getShopId()) >= 0}
                            <div class="unavailable-text">
                                <span>
                                    Produit indisponible dans votre magasin
                                </span>
                            </div>
                        {/if}
                    {/if}
                </div>
            </div>

            <div class="product-infos">
                <div class="header">
                    <div class="title">
                        <h2>{product.brand}</h2>
                        <div class="p-title">
                            <p>{product.name}</p>
                        </div>
                    </div>

                    <div
                        id="group-price"
                        class={product.MEA === "tel" ? "is-hidden" : ""}
                    >
                        <div class="price">
                            <ProductPrice {product} />
                        </div>
                        <div id="promo">
                            <PastillePromotion {product} />
                        </div>
                    </div>
                </div>

                {#if showCartButtons}
                    <div class="add-to-cart">
                        {#if cartProduct && cartProduct.quantity > 0}
                            <div class="input-qte">
                                <InputQuantity
                                    label="Qté"
                                    {quantity}
                                    on:update-quantity={handlerQTE}
                                />
                            </div>
                        {/if}
                        <button
                            class="bg-dark-black button-qte"
                            on:click={() => qteUpdate()}
                        >
                            <i
                                class="fas fa-shopping-bag"
                                style="margin-right: 1px;"
                            />
                            Ajouter au panier
                        </button>
                    </div>
                {/if}
                {#if (product.description && product.description !== "") || (product.origine && product.origine !== "")}
                    <div id="description">
                        <h3
                            class:close={!opensPart.description}
                            on:click={() => openClosePart("description")}
                        >
                            Description
                        </h3>

                        {#if opensPart.description}
                            <p>
                                {product.description}

                                {#if product.origine && product.origine !== ""}
                                    <br />{product.origine}
                                {/if}
                                {#if product.ref_price && product.ref_price === "advantage"}
                                    <br />(1) Prix conseillé par la coopérative
                                    au(x) point(s) de vente participant à
                                    l'opération commerciale.
                                {/if}
                            </p>
                        {/if}

                        {#if !opensPart.description}
                            <span
                                class="icon"
                                on:click={() => openClosePart("description")}
                            >
                                <i class="fas fa-chevron-down" />
                            </span>
                        {:else}
                            <span
                                class="icon"
                                on:click={() => openClosePart("description")}
                            >
                                <i class="fas fa-chevron-up" />
                            </span>
                        {/if}
                    </div>
                {/if}
                {#if product.techDocs && product.techDocs.length > 0}
                    <div id="docs">
                        <h3
                            class:close={!opensPart.docs}
                            on:click={() => openClosePart("docs")}
                        >
                            Documents
                        </h3>
                        {#if opensPart.docs}
                            <div id="docs-grid">
                                {#each product.techDocs as doc}
                                    <div
                                        class="doc-item"
                                        on:click={() =>
                                            ddlFile(doc.name, doc.url)}
                                    >
                                        <div class="doc-file">
                                            <i class="far fa-file fa-lg" />
                                        </div>
                                        <div class="doc-name">
                                            {doc.name}
                                        </div>
                                    </div>
                                {/each}
                            </div>
                        {/if}
                        {#if !opensPart.docs}
                            <span
                                class="icon"
                                on:click={() => openClosePart("docs")}
                            >
                                <i class="fas fa-chevron-down" />
                            </span>
                        {:else}
                            <span
                                class="icon"
                                on:click={() => openClosePart("docs")}
                            >
                                <i class="fas fa-chevron-up" />
                            </span>
                        {/if}
                    </div>
                {/if}
                {#if product.displaySupplierRef && product.supplierRef !== ""}
                    <div id="supplierRef">
                        <h3>Référence fournisseur</h3>
                        {product.supplierRef}
                    </div>
                {/if}
                {#if product.legalNotices && product.legalNotices.length > 0 && (product.legalNotices[0] !== "" || product.legalNotices.length > 1)}
                    <div id="other-infos">
                        <h3>Autres informations</h3>
                        {#each product.legalNotices as notice}
                            <div class="notice">{notice}</div>
                        {/each}
                    </div>
                {/if}
                {#if product.mandatStartDate && product.mandatStartDate !== ""}
                    <div id="validity">
                        <h3>Validité</h3>
                        Offre valable du {new moment(
                            product.mandatStartDate
                        ).format("DD/MM/YYYY")}
                        au {new moment(product.mandatEndDate).format(
                            "DD/MM/YYYY"
                        )}
                    </div>
                {/if}
            </div>
        </div>

        <div class="bottom">
            {#if product && product.pictos && product.logos && product.pictos.length + product.logos.length !== 0}
                <div id="pictos">
                    {#each product.logos as logo}
                        <img
                            on:error={() => {
                                logo = "/no_visual.jpg";
                            }}
                            src="{logo}{logo?.includes('datafiles')
                                ? '?g=80x80'
                                : ''}"
                            alt="Chargement du logo"
                            class:is-icon={!logo?.includes("datafiles")}
                        />
                    {/each}
                    {#each product.pictos as picto}
                        <img
                            on:error={handleImgError}
                            src="{picto}{picto?.includes('datafiles')
                                ? '?g=80x80'
                                : ''}"
                            alt="Chargement du picto"
                            class:is-icon={!picto?.includes("datafiles")}
                        />
                    {/each}
                </div>
            {/if}
        </div>

        {#if crossSell && otherProducts !== null && otherProducts.length > 1 && !crossSellLoader}
            <div id="other-products">
                <h3>Produits pouvant également vous intéresser</h3>
                <div
                    id="list-other-products"
                    style="position: relative; width:100%;"
                >
                    <ProductCarousel
                        products={otherProducts.filter(
                            (p) => p.uid != product.uid
                        )}
                        perPage={nbcol}
                    />
                </div>
            </div>
        {:else if crossSell && crossSellLoader}
            <div id="other-products">
                <div id="crossSellLoader" class="mt-5">
                    Chargement des produits similaires...
                    <progress
                        class="mt-5 progress is-large txt-dark-green"
                        max="100"
                        >0%
                    </progress>
                </div>
            </div>
        {/if}
    </div>
{/if}

<style lang="scss">
    @import "../styles/variables.scss";

    #sheet {
        display: flex;
        flex-direction: column;
        background-color: $beige;
        padding: 0.5rem;
        border-radius: 8px;
    }

    .return {
        span {
            color: $dark-black;
            font-weight: bold;
            font-size: 1.2em;
            cursor: pointer;
        }
    }

    .prod {
        position: relative;
        display: flex;
        flex-direction: row;
        background-color: white;
        @media (max-width: $mobile-breakpoint) {
            flex-direction: column;
        }
    }

    .product-infos {
        display: flex;
        flex-direction: column;
        width: 50%;
        @media (max-width: $mobile-breakpoint) {
            width: 100%;
            padding: 0.5rem;
        }
        gap: 1rem;
        margin-bottom: 1rem;
    }

    #group-price {
        display: flex;
        flex-direction: row;
        align-items: center;
        .price {
            display: flex;
            flex-direction: row;
        }
    }

    .add-to-cart {
        display: inline-flex;
        .input-qte {
            max-width: 70%;
        }

        @media (max-width: $mobile-breakpoint) {
            display: flex;
            flex-direction: column;
        }
    }

    .bottom {
        background-color: white;
    }

    .button-qte {
        align-items: center;
        height: 40px;
        padding: 0 15px;
        margin: 5px;
        line-height: 38px;
        font-size: 15px;
        border-radius: 8px;
        color: #ffffff;
        border: unset;
        cursor: pointer;
        text-decoration: unset;
        font-style: normal;
        font-weight: 500;
    }

    .title {
        margin: 20px 0;
        font-size: 3rem;
        font-weight: bold;
        font-family: "Gilmer Bold";

        h2 {
            text-transform: uppercase;
            font-weight: normal;
            font-family: "Gilmer Medium";
            font-size: 1rem;
        }

        .p-title {
            p {
                text-transform: uppercase;
                font-size: 2rem !important;
                font-family: "Gilmer Medium";
                @media (max-width: $mobile-breakpoint) {
                    font-size: 1.5rem !important;
                }
            }
        }
    }

    .medias {
        display: flex;
        flex-direction: row;
        width: 50%;
        padding-left: 0.5rem;
        @media (max-width: $mobile-breakpoint) {
            width: 100%;
            flex-direction: column;
            padding-left: unset;
        }
        .picture {
            display: flex;
            align-items: flex-start;
            width: 100%;
            img,
            iframe {
                object-fit: scale-down;
                height: 400px;
                width: 100%;
                padding: 1rem;
                &.unavailable {
                    opacity: 0.4;
                }
            }
        }

        .thumbnail-list {
            display: flex;
            justify-content: center;
            flex-direction: column;
            z-index: 10;

            .thumbnail {
                float: left;
                height: 70px;
                width: 70px;
                border: 1px solid $grey-light;
                position: relative;
                cursor: pointer;
                &.active {
                    border: 1px solid $grey-darken;
                }

                img {
                    object-fit: contain;
                    height: 100%;
                    width: 100%;
                }

                .play {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    color: red;

                    .player-logo {
                        height: 100%;
                        width: 100%;
                    }
                }
            }

            @media (max-width: $mobile-breakpoint) {
                flex-direction: row;
                max-width: 100%;
                overflow: auto;
            }
        }
    }

    #description,
    #docs {
        font-size: 0.9rem;
        white-space: pre-line;
        position: relative;
        h3 {
            cursor: pointer;
            font-family: "Gilmer Bold";
            font-weight: bold;
            font-size: 1rem;
            color: black;
            &.close {
                border-bottom: 1px solid $grey-light;
            }
        }

        p {
            font-family: "Gilmer Medium";
            font-size: 0.8rem;
        }

        .icon {
            position: absolute;
            top: 0;
            right: 0.5rem;
        }
    }

    #docs-grid {
        margin-top: 1rem;
        display: grid;
        column-gap: 0.5rem;
        row-gap: 0.5rem;
        grid-template-columns: 1fr 1fr;
        @media (max-width: $mobile-breakpoint) {
            grid-template-columns: 1fr;
        }
        .doc-item {
            display: flex;
            flex-direction: row;
            text-align: center;
            cursor: pointer;

            .doc-file {
                color: black;
                margin-right: 2px;
            }
        }
    }

    #other-infos {
        font-size: 0.8rem;
    }

    #pictos {
        border-bottom: 1px solid $grey-light;
        display: flex;
        align-items: center;
        width: 100%;
        flex-direction: row;
        img {
            float: left;
            padding: 10px;
        }
    }

    #other-products {
        width: 100%;
        float: left;
        padding-left: 0.5rem;
        margin-top: 2.5rem;
        @media (max-width: $mobile-breakpoint) {
            padding-bottom: $menu-mobile-height;
        }
        #list-other-products {
            overflow-x: scroll;
            overflow-y: hidden;
            display: flex;
            flex-flow: row;
            margin-top: 0.5rem;
        }

        #crossSellLoader {
            width: 100%;
            text-align: center;
            font-size: 2em;
            color: $dark-green;
        }
    }
</style>
